import React, { useCallback, useEffect, useMemo } from "react";

import debounce from "lodash.debounce";
import moment from "moment";
import { useTranslation } from "react-i18next";

import ContentTooltipItem from "./ContentTooltipItem";
import { API_DATE_FORMAT } from "../../../constants/dateFormat";
import useFAST from "../../../contexts/FASTContext";
import useTimebounds from "../../../hooks/useTimebounds";
import useTimelineItems from "../../../hooks/useTimelineItems";
import Tooltip from "../../tooltip/Tooltip";

import type { Moment } from "moment";
import type { JSX } from "react";

interface ContentTooltipFillerProps {
  isOverLimitDuration: boolean;
  isFillerDurationUnvailableOnPreviousDay?: boolean;
  isFillerDurationUnvailableOnNextDay?: boolean;
  duration: number;
  start: Moment;
  end: Moment;
}

function ContentTooltipFiller({
  isOverLimitDuration,
  isFillerDurationUnvailableOnNextDay,
  isFillerDurationUnvailableOnPreviousDay,
  duration,
  start,
  end,
}: ContentTooltipFillerProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  if (isOverLimitDuration) {
    return <React.Fragment>{t("fillerDurationOverLimit")}</React.Fragment>;
  }

  if (isFillerDurationUnvailableOnNextDay) {
    return (
      <React.Fragment>{t("fillerDurationUnvailableOnNextDay")}</React.Fragment>
    );
  }

  if (isFillerDurationUnvailableOnPreviousDay) {
    return (
      <React.Fragment>
        {t("fillerDurationUnvailableOnPreviousDay")}
      </React.Fragment>
    );
  }

  return (
    <ContentTooltipItem
      duration={duration}
      endTime={end}
      endTimeLabel={t("next", { ns: "editor-utils" })}
      startTime={start}
      startTimeLabel={t("previous", { ns: "editor-utils" })}
      title={t("filler")}
    />
  );
}

interface TimelineFillerTooltipProps {
  setFillerTimeoutRef: (timeout: NodeJS.Timeout) => void;
}

const TIMEOUT_DELAY = 200;

const FILLER_LIMIT_DURATION = 1000 * 3600 * 6;

export default function TimelineFillerTooltip({
  setFillerTimeoutRef,
}: TimelineFillerTooltipProps): JSX.Element | null {
  const { dispatch, state } = useFAST();
  const {
    currentTimeslots,
    timelineDataPartialFetched,
    computedItemsTypeKeyName,
  } = useTimelineItems();
  const { currentDay } = useTimebounds();

  const nextDay = moment(currentDay).add(1, "day").format(API_DATE_FORMAT);
  const previousDay = moment(currentDay)
    .subtract(1, "day")
    .format(API_DATE_FORMAT);

  const isPartialDataOnNextDay =
    timelineDataPartialFetched[nextDay]?.[computedItemsTypeKeyName];

  const isPartialDataOnPreviousDay =
    timelineDataPartialFetched[previousDay]?.[computedItemsTypeKeyName];

  const { fillerTooltip } = state;
  const { clickPosition, timestamp } = fillerTooltip ?? {};

  const clickedTimeslot = useMemo(
    () =>
      currentTimeslots?.timeslot?.availableTimeSlots.find(
        (timeslot) =>
          moment(timeslot.start).isBefore(timestamp) &&
          moment(timeslot.end).isAfter(timestamp),
      ),
    [currentTimeslots, timestamp],
  );

  const debouncedTimeout = useMemo(
    () =>
      debounce(
        () => {
          const timeout = setTimeout(() => {
            dispatch({
              type: "CLEAN_FILLER_TOOLTIP",
            });
          }, TIMEOUT_DELAY);
          setFillerTimeoutRef(timeout);
        },
        TIMEOUT_DELAY,
        {
          leading: true,
          trailing: false,
        },
      ),
    [dispatch, setFillerTimeoutRef],
  );

  const cleanFillerTooltip = useCallback(() => {
    if (!fillerTooltip) return;
    debouncedTimeout();
  }, [fillerTooltip, debouncedTimeout]);

  useEffect(() => {
    window.addEventListener("mousemove", cleanFillerTooltip);

    return () => {
      window.removeEventListener("mousemove", cleanFillerTooltip);
    };
  });

  const { start = 0, end = 0 } = clickedTimeslot ?? {};
  const nextProgramStart = end + 1;
  const duration = nextProgramStart - start;
  const isOverLimitDuration = duration > FILLER_LIMIT_DURATION;

  const isOnCurrentDay = moment(start).isSame(currentDay, "day");
  const nextProgramStartHours = moment(nextProgramStart).format("HH:mm:ss");
  const previousProgramStartHours = moment(start).format("HH:mm:ss");

  const isFillerDurationUnvailableOnNextDay =
    !isOverLimitDuration &&
    !isOnCurrentDay &&
    isPartialDataOnNextDay &&
    nextProgramStartHours === "12:00:00";

  const isFillerDurationUnvailableOnPreviousDay =
    !isOverLimitDuration &&
    !isOnCurrentDay &&
    isPartialDataOnPreviousDay &&
    previousProgramStartHours === "12:00:00";

  if (!clickedTimeslot || !duration) return null;

  return (
    <Tooltip
      id="timeline-filler-tooltip"
      isOpen={!!fillerTooltip}
      position={clickPosition}
    >
      <ContentTooltipFiller
        duration={duration}
        end={moment(nextProgramStart)}
        isOverLimitDuration={isOverLimitDuration}
        start={moment(start)}
        isFillerDurationUnvailableOnNextDay={
          isFillerDurationUnvailableOnNextDay
        }
        isFillerDurationUnvailableOnPreviousDay={
          isFillerDurationUnvailableOnPreviousDay
        }
      />
    </Tooltip>
  );
}
